import React, { Component } from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { Link, browserHistory } from 'react-router';
import _find from 'lodash/find';
import { Tag } from '@zola/zola-ui';
import { getCardSettings } from 'cards/util/cardSettingsUtils';
import { CARD_TYPES } from 'cards/constants/CardTypes';

// Components
import SingleSampleLayout from 'cards/components/common/SingleSample/Layout/Layout';
import SingleSampleModalLinkPLP from 'cards/components/common/SingleSample/SingleSampleModalLinkPLP/SingleSampleModalLinkPLP';
import compactCurrencyFilter from 'components/product/PricingUtils';
import ProductColorPicker from 'cards/components/common/ProductColorPicker/ProductColorPicker';

// Selectors
import { getMultiSampleState } from 'cards/selectors/singlesample';

// Actions
import { setMultiSampleSelected as setMultiSampleSelectedAction } from 'cards/actions/singlesample';

// Util
import FeatureFlags from '../../../../util/featureFlags';
import styles from './styles.module.less';
import { srcsetHelper } from '../../../util/imageHelper';

import './ListingDetail.less';

function isMultiSampleCheckboxDisabled(card, multiSampleState) {
  return (
    !multiSampleState.selected[card.suiteUUID] &&
    Object.keys(multiSampleState.selected).length >= multiSampleState.count.limit
  );
}

export class ListingDetailBase extends Component {
  constructor(props) {
    super(props);

    this.handleSelectColor = this.handleSelectColor.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleTrackingProductClickedEvent = this.handleTrackingProductClickedEvent.bind(this);
    const {
      card: { colors },
      filterColor,
      initialVariationColor,
      filterMultiColor,
    } = props;

    // default activeColorObject is the first object in colors array
    let activeColorObject = colors && colors[0];

    if (filterMultiColor) {
      // Find the first card color that matches one of the filterMulticolors
      const foundColorObject = _find(
        colors,
        data =>
          data.nrf_color_code_label &&
          filterMultiColor.indexOf(data.nrf_color_code_label.toUpperCase()) > -1
      );

      if (foundColorObject && foundColorObject.value) {
        // check that foundColorObject is truthy and has a .value that is also truthy, if both
        // set activeColorObject to be the foundColorObject
        activeColorObject = foundColorObject;
      }
    } else if (filterColor || initialVariationColor) {
      // if there is a filter color, find the color with nrf_color_code label that matches
      // the filterColor
      const foundColorObject = _find(colors, data =>
        filterColor
          ? data.nrf_color_code_label.toUpperCase() === filterColor.toUpperCase()
          : data.value.toUpperCase() === initialVariationColor.toUpperCase()
      );
      if (foundColorObject && foundColorObject.value) {
        // check that foundColorObject is truthy and has a .value that is also truthy, if both
        // set activeColorObject to be the foundColorObject
        activeColorObject = foundColorObject;
      }
    }

    this.state = {
      activeColor: activeColorObject && activeColorObject.value,
      activeThumbnail: activeColorObject && activeColorObject.plp_front_image_url,
    };
  }

  handleSelectColor(colorObject) {
    const { card, multiSampleState, setMultisampleSelected } = this.props;

    if (multiSampleState.enabled && multiSampleState.selected[card.suiteUUID]) {
      setMultisampleSelected(card, !!multiSampleState.selected[card.suiteUUID], colorObject.value);
    }

    this.setState({
      activeColor: colorObject.value,
      activeThumbnail: colorObject.plp_front_image_url,
    });
  }

  handleClick(e, pathname) {
    const {
      card,
      onSetInitialVariationColor,
      lightView,
      plpOnClickCallback,
      allowMultiSample,
      multiSampleState,
      setMultisampleSelected,
    } = this.props;

    const { activeColor } = this.state;

    if (allowMultiSample && multiSampleState.enabled) {
      const checkboxDisabled = isMultiSampleCheckboxDisabled(card, multiSampleState);

      e.preventDefault();

      if (!checkboxDisabled) {
        setMultisampleSelected(card, !multiSampleState.selected[card.suiteUUID], activeColor);
      }
      return;
    }

    if (activeColor && !lightView) {
      onSetInitialVariationColor(activeColor);
      if (!lightView) e.preventDefault();
      if (plpOnClickCallback) plpOnClickCallback();
      this.handleTrackingProductClickedEvent();
      browserHistory.push(pathname);
    }
  }

  handleTrackingProductClickedEvent() {
    const { handleTrackCardProductClicked, card, basePlanningUrl } = this.props;
    const { suiteUUID, uuid, suiteName, min_price_cents: minPriceCents, type, colors } = card;
    const { urlSlug } = getCardSettings(type);
    const imageUrl = colors && colors[0] && colors[0].plp_front_image_url;
    const metaData = {
      product_id: suiteUUID,
      sku: uuid,
      category: 'Paper',
      name: suiteName,
      // brand, omitting since does not apply
      variant: uuid,
      price: (minPriceCents / 100).toFixed(2),
      quantity: 1,
      // position, omitting since does not apply
      url: `${basePlanningUrl}/${urlSlug}/${suiteUUID}`,
      image_url: imageUrl || 'N/A',
      location: 'PLP',
      section: null,
    };

    handleTrackCardProductClicked({
      name: 'Product Clicked',
      properties: metaData,
    });
  }

  render() {
    const {
      card,
      basePlanningUrl,
      shouldExpand,
      lightView,
      collaborator,
      thirdParty,
      activeCardEvent,
      showSingleSampleModalLink,
      allowMultiSample,
      multiSampleState,
      setMultisampleSelected,
    } = this.props;
    const { activeColor, activeThumbnail } = this.state;

    const { urlSlug } = getCardSettings(card.type);

    const pathname = `${basePlanningUrl}/${urlSlug}/${card.suiteUUID}`;

    let priceElement = null;

    const isMultiSampleSelection = !!multiSampleState.selected[card.suiteUUID];

    let checkBoxElement = null;

    let checkboxDisabled = false;

    if (allowMultiSample && multiSampleState.enabled) {
      checkboxDisabled = isMultiSampleCheckboxDisabled(card, multiSampleState);

      checkBoxElement = (
        <SingleSampleLayout.Checkbox
          className={styles.multiSampleCheckbox}
          id={`sample-checked__${card.suiteUUID}`}
          disabled={checkboxDisabled}
          label=""
          checked={isMultiSampleSelection}
          onChange={() => setMultisampleSelected(card, !isMultiSampleSelection, activeColor)}
        />
      );
    } else {
      priceElement =
        activeCardEvent === CARD_TYPES.ctd ? (
          <p className="product-pricing red">Free with paper purchases</p>
        ) : (
          <p className="product-pricing red">
            From {compactCurrencyFilter(card.min_price_cents / 100)} Ea.
          </p>
        );
    }

    // TODO: remove feature flag and make classes permenant.
    const shouldShowRedesign = FeatureFlags.get('invitesSingleSampleModal');

    return (
      <div
        className={cx('listing-detail', styles.tileLinkBox, {
          [styles.leftAlign]: shouldShowRedesign,
          [styles.disabledSampleTile]: checkboxDisabled,
        })}
      >
        <Link
          className="link"
          to={{ pathname }}
          target={lightView ? '_blank' : '_self'}
          onClick={e => this.handleClick(e, pathname)}
        >
          <div className="image image--is-bigger">
            {activeThumbnail && (
              <img
                className={cx({
                  big: shouldExpand,
                  [styles.dropShadow]: shouldShowRedesign,
                  [styles.selectedSample]:
                    allowMultiSample && multiSampleState.enabled && isMultiSampleSelection,
                  [styles.largerWhiteBorder]: shouldShowRedesign,
                })}
                src={`${activeThumbnail}?w=465`}
                srcSet={srcsetHelper(activeThumbnail, 465)}
                alt={card.name}
              />
            )}
          </div>
          <div>
            <div className={`details${collaborator ? ' details--less-margin' : ''}`}>
              <div>
                <div className={cx(['product-name gray', styles.productNameSection])}>
                  <span className={cx({ [styles.productName]: shouldShowRedesign })}>
                    {card.suiteName && card.suiteName.split(' - ')[1]}
                  </span>
                  {thirdParty && <div className={styles.isZolaXTag}>Zola x Featured Artist</div>}
                  {collaborator && (
                    <div
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        e.nativeEvent.stopImmediatePropagation();
                        browserHistory.push(`/wedding-planning/${collaborator.slug}/shop`);
                      }}
                      className={styles.isZolaXTag}
                    >
                      Zola x {collaborator.label}
                    </div>
                  )}
                </div>
              </div>
              {priceElement}
            </div>
          </div>
        </Link>
        <div>
          {!lightView && (
            <ProductColorPicker
              colors={card.colors}
              activeColor={activeColor}
              maxColorsToShow={5}
              onSelect={colorObject => this.handleSelectColor(colorObject)}
              link={pathname}
              swatchWidth={shouldShowRedesign ? 24 : null}
            />
          )}
          {card.foil && <div className="foil-label gold" />}
          {card.letterpress && (
            <Tag className={styles.isLetterpressTag} size="lg">
              LETTERPRESS
            </Tag>
          )}
          {showSingleSampleModalLink && !(allowMultiSample && multiSampleState.enabled) && (
            <div className={styles.singleSampleLinkBox}>
              <SingleSampleModalLinkPLP card={card} initialColor={activeColor} />
            </div>
          )}
        </div>
        {checkBoxElement}
      </div>
    );
  }
}

ListingDetailBase.propTypes = {
  card: PropTypes.shape({
    colors: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    foil: PropTypes.bool,
    min_price_cents: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    suiteUUID: PropTypes.string.isRequired,
    type: PropTypes.string,
    uuid: PropTypes.string,
  }).isRequired,
  collaborator: PropTypes.shape({}),
  onSetInitialVariationColor: PropTypes.func.isRequired,
  filterColor: PropTypes.string,
  filterMultiColor: PropTypes.arrayOf(PropTypes.string),
  basePlanningUrl: PropTypes.string,
  shouldExpand: PropTypes.bool,
  initialVariationColor: PropTypes.string,
  lightView: PropTypes.bool,
  handleTrackCardProductClicked: PropTypes.func,
  plpOnClickCallback: PropTypes.func,
  allowMultiSample: PropTypes.bool,
  multiSampleState: PropTypes.shape({
    selected: PropTypes.shape({}),
    enabled: PropTypes.bool,
  }),
  setMultisampleSelected: PropTypes.func,
};

ListingDetailBase.defaultProps = {
  allowMultiSample: false,
  multiSampleState: {
    selected: {},
    enabled: false,
  },
  setMultisampleSelected: () => {},
};

export const mapStateToProps = state => {
  return {
    multiSampleState: getMultiSampleState(state),
  };
};

export const mapDispatchToProps = dispatch => {
  return {
    setMultisampleSelected: (card, selected, initialColor) =>
      dispatch(setMultiSampleSelectedAction(card, selected, initialColor)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ListingDetailBase);



// WEBPACK FOOTER //
// ./src/cards/components/CardListing/ListingDetail/ListingDetail.jsx